import { useState, useCallback } from "react";
import type { ReactNode } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { WarningIcon } from "@phosphor-icons/react/Warning";
import { Icon } from "../../Icons/Icon";
import { Tooltip } from "../../Feedback/Tooltip";
import type { TabCountColor, TabWarning } from "../Tab";

// `on-bright` for the three light backgrounds, because none of gold, green or
// orange flips with the theme — ink that does would turn near-white on them in
// dark mode.
export const countColorClasses: Record<TabCountColor, string> = {
  gold: "bg-gold text-on-bright",
  green: "bg-green text-on-bright",
  red: "bg-red text-white",
  blue: "bg-blue text-white",
  orange: "bg-orange text-on-bright",
  purple: "bg-purple text-white",
  grey: "bg-foreground/30 text-white",
};

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function TabTrigger_({
  id,
  label,
  testId,
  count,
  disabled,
  icon,
  warning,
  isActive,
  dense,
}: {
  id: string;
  label: string;
  testId?: string | undefined;
  count?: number | undefined;
  countColor?: TabCountColor | undefined;
  disabled?: boolean | undefined;
  icon?: ReactNode;
  warning?: TabWarning | undefined;
  isActive: boolean;
  dense?: boolean | undefined;
}) {
  const [keyboardFocused, setKeyboardFocused] = useState(false);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLButtonElement>) => {
    setKeyboardFocused(e.target.matches(":focus-visible"));
  }, []);

  // Comfortable density: text tabs on the strip's bottom divider; the
  // active tab gets a blue underline and dark text, the rest quiet muted text.
  const sizing = dense ? "gap-1 text-[13px]" : "gap-1.5 text-sm";

  const triggerClass = `relative inline-flex items-center whitespace-nowrap transition-colors focus:outline-none ${sizing} ${
    dense ? "pb-1.5 pt-0.5" : "pb-2.5 pt-1"
  } ${
    disabled
      ? "cursor-not-allowed font-semibold text-faint -mb-px border-b-2 border-transparent"
      : isActive
        ? "cursor-pointer font-semibold text-foreground -mb-px border-b-2 border-brand"
        : "cursor-pointer font-normal text-foreground -mb-px border-b-2 border-transparent"
  }`;

  return (
    <TabsPrimitive.Trigger
      value={id}
      {...(testId !== undefined && { "data-testid": testId })}
      disabled={disabled === true}
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={() => setKeyboardFocused(false)}
      className={triggerClass}
    >
      {icon && (
        <span
          className={isActive ? "text-brand-text" : "text-muted-foreground"}
        >
          {icon}
        </span>
      )}
      {warning && (
        <Tooltip content={warning.tooltip}>
          <Icon
            icon={WarningIcon}
            size={dense ? "xs" : "sm"}
            color="text-gold"
            weight="fill"
          />
        </Tooltip>
      )}
      <span
        className={
          keyboardFocused && !isActive
            ? `underline decoration-muted-foreground decoration-2 underline-offset-4`
            : ""
        }
      >
        {label}
      </span>
      {count !== undefined && (
        <span
          {...(testId !== undefined && { "data-testid": `${testId}-count` })}
          className={`rounded-full text-center font-extrabold ${
            dense
              ? "min-w-[16px] px-1 py-px text-[10px] leading-tight"
              : "min-w-[20px] px-1.5 py-px text-[11px] leading-tight"
          } ${
            isActive
              ? "bg-brand/15 text-brand-text"
              : "bg-foreground/10 text-muted-foreground"
          }`}
        >
          {count}
        </span>
      )}
    </TabsPrimitive.Trigger>
  );
}
